import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FileText, Filter, Plus, Search } from "lucide-react";
import useAxios from "../../../../../hooks/useAxios";
import { useNavigate } from "react-router-dom";
import { resolveDatasetsCentreDetailRoute } from "../../../../../shared/lib/routesResolver";
import AddDatasetDialog from "./dataset-detail/AddDatasetDialog";
import DeleteDatasetDialog from "./dataset-detail/DeleteDatasetDialog";
import { Input } from "../../../../../shared/components/input/Input";
import {
  BaseSelect,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../../shared/components/select/BaseSelect";
import useToastWrapper from "../../../../../hooks/useToastWrapper";
import { Button } from "../../../../../shared/components/button/Button";
import DatasetCard from "./DatasetCard";
import DatasetSkeleton from "./DatasetSkeleton";
import {
  API_DATASETS,
  API_DATASETS_DETAILS,
} from "../../../../../shared/lib/apiEndpointsResolver";

const DatasetsPage = () => {
  const axios = useAxios();
  const [isLoading, setIsLoading] = useState(true);
  const [datasets, setDatasets] = useState([]);
  const { t } = useTranslation();
  const { display, displayError, displayUnexpectedError } = useToastWrapper();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [isAddDatasetDialogOpen, setIsAddDatasetDialogOpen] = useState(false);
  const navigate = useNavigate();
  const [datasetToDelete, setDatasetToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const filteredDatasets = useMemo(() => {
    if (isLoading) {
      return [];
    }

    return datasets.filter((dataset) => {
      const matchesSearch =
        dataset.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        dataset.description?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "active" && dataset.active) ||
        (statusFilter === "inactive" && !dataset.active);

      return matchesSearch && matchesStatus;
    });
  }, [datasets, searchTerm, statusFilter, isLoading]);

  const openAddDatasetDialog = () => {
    setIsAddDatasetDialogOpen(true);
  };

  const onAddDatasetSuccessfully = (newDataset) => {
    navigate(resolveDatasetsCentreDetailRoute(newDataset.id));
    display(
      t("datasetsCentre.common.success"),
      t("datasetsCentre.datasets.addSuccess", { code: newDataset.code }),
    );
  };

  const checkDatasetCodeDuplicated = (code) => {
    return datasets.some((d) => d.code === code.trim());
  };

  const displayDeleteDatasetDialog = (dataset) => {
    setDatasetToDelete(dataset);
  };

  const cancelDeleteDataset = () => {
    setDatasetToDelete(null);
  };

  const deleteDataset = async (id) => {
    setIsDeleting(true);

    try {
      const result = await axios.delete(API_DATASETS_DETAILS(id));

      if (result.status === 204) {
        const dataset = datasets.filter((d) => d.id === id)[0];
        setDatasets((prev) => prev.filter((d) => d.id !== id));
        display(
          t("datasetsCentre.common.success"),
          t("datasetsCentre.datasets.deleteSuccess", { code: dataset.code }),
        );
      } else {
        console.error(result.data);
        displayError(result.data);
      }
    } catch (error) {
      console.log(error);
      displayUnexpectedError();
    } finally {
      setIsDeleting(false);
      setDatasetToDelete(null);
    }
  };

  useEffect(() => {
    const loadDatasets = async () => {
      setIsLoading(true);

      try {
        const response = await axios.get(API_DATASETS);
        const data = response.data;
        setDatasets(data);
      } catch (error) {
        const data = error?.response?.data;
        console.error(data);
        displayUnexpectedError();
      } finally {
        setIsLoading(false);
      }
    };

    loadDatasets();
  }, []);

  return (
    <>
      <AddDatasetDialog
        isDatasetDialogOpen={isAddDatasetDialogOpen}
        setIsDatasetDialogOpen={setIsAddDatasetDialogOpen}
        onAddDatasetSuccessfully={onAddDatasetSuccessfully}
        checkDatasetCodeDuplicated={checkDatasetCodeDuplicated}
      />
      <DeleteDatasetDialog
        dataset={datasetToDelete}
        isDeleting={isDeleting}
        onCancel={cancelDeleteDataset}
        onDeleteHandler={deleteDataset}
      />
      <div className="w-full transition-colors p-4">
        <div className="mb-8 flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              {t("datasetsCentre.datasets.title")}
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              {t("datasetsCentre.datasets.description")}
            </p>
          </div>
        </div>

        <div className="mb-6 flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 h-4 w-4" />
            <Input
              placeholder={t("datasetsCentre.datasets.searchPlaceholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              disabled={isLoading}
              className="pl-10 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 placeholder:text-gray-500 dark:placeholder:text-gray-400 disabled:opacity-50"
            />
          </div>
          <BaseSelect
            value={statusFilter}
            onValueChange={setStatusFilter}
            disabled={isLoading}
          >
            <SelectTrigger className="w-full sm:w-48 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 disabled:opacity-50">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue
                placeholder={t(
                  "datasetsCentre.datasets.statusFilterPlaceholder",
                )}
              />
            </SelectTrigger>
            <SelectContent className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
              <SelectItem
                value="all"
                className="text-gray-900 dark:text-gray-100 focus:bg-gray-100 dark:focus:bg-gray-700"
              >
                {t("datasetsCentre.common.allStatus")}
              </SelectItem>
              <SelectItem
                value="active"
                className="text-gray-900 dark:text-gray-100 focus:bg-gray-100 dark:focus:bg-gray-700"
              >
                {t("datasetsCentre.common.onlyActiveStatus")}
              </SelectItem>
              <SelectItem
                value="inactive"
                className="text-gray-900 dark:text-gray-100 focus:bg-gray-100 dark:focus:bg-gray-700"
              >
                {t("datasetsCentre.common.onlyInactiveStatus")}
              </SelectItem>
            </SelectContent>
          </BaseSelect>
          <Button onClick={openAddDatasetDialog} className="gap-2">
            <Plus className="w-4 h-4" />
            {t("datasetsCentre.datasets.addButton")}
          </Button>
        </div>

        {!isLoading && (
          <div className="mb-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t("datasetsCentre.datasets.showingCount", {
                count: filteredDatasets.length,
                total: datasets.length,
              })}
            </p>
          </div>
        )}

        {!isLoading && filteredDatasets.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredDatasets.map((dataset) => (
              <DatasetCard
                key={dataset.id}
                dataset={dataset}
                onDelete={() => displayDeleteDatasetDialog(dataset)}
              />
            ))}
          </div>
        )}

        {isLoading && <DatasetSkeleton />}

        {filteredDatasets.length === 0 && !isLoading && (
          <div className="text-center py-12">
            <FileText className="h-12 w-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              {t("datasetsCentre.datasets.noDatasets")}
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              {t("datasetsCentre.datasets.noDatasetsHint")}
            </p>
          </div>
        )}
      </div>
    </>
  );
};

export default DatasetsPage;
